import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, timer, fromEvent, merge } from 'rxjs';
import { map, switchMap, debounceTime, distinctUntilChanged, startWith, catchError, withLatestFrom, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class NetworkStatusService {
  private isOnline$ = new BehaviorSubject<boolean>(navigator.onLine);
  private networkQuality$ = new BehaviorSubject<'good'|'poor'|'offline'>('good');
  private readonly CHECK_INTERVAL = 30000; // 30 seconds
  private readonly ENDPOINT = '/api/healthcheck'; // Replace with actual health check endpoint

  constructor() {
    this.initializeNetworkMonitoring();
  }

  private initializeNetworkMonitoring(): void {
    // Listen for browser online/offline events
    merge(
      fromEvent(window, 'online').pipe(map(() => true)),
      fromEvent(window, 'offline').pipe(map(() => false))
    ).subscribe(this.isOnline$);

    // Start periodic quality checks when online
    this.isOnline$.pipe(
      withLatestFrom(this.checkConnectionQuality().pipe(take(1))),
      switchMap(([isOnline, status]) => {
        if (!isOnline) {
          this.networkQuality$.next('offline');
          return timer(0);
        }
        else{
          this.networkQuality$.next(status);
        }
        return timer(0, this.CHECK_INTERVAL).pipe(
          switchMap(() => this.checkConnectionQuality())
        );
      })
    ).subscribe();
  }

  private checkConnectionQuality(): Observable<'good'|'poor'|'offline'> {
  const startTime = Date.now();

  return new Observable<'good' | 'poor' | 'offline'>(observer => {
    fetch(this.ENDPOINT, {
      method: 'HEAD',
      cache: 'no-cache'
    })
    .then(() => {
      const latency = Date.now() - startTime;
      if (latency < 1000) {
        observer.next('good');
      } else {
        observer.next('poor');
      }
      observer.complete();
    })
    .catch(() => {
      observer.next('offline');
      observer.complete();
    });
  }).pipe(
    debounceTime(100),
    distinctUntilChanged(),
    startWith(this.networkQuality$.value),
    catchError(() => [this.networkQuality$.value])
  );
}

  getNetworkStatus(): Observable<boolean> {
    return this.isOnline$.asObservable();
  }

  getNetworkQuality(): Observable<'good'|'poor'|'offline'> {
    return this.networkQuality$.asObservable();
  }
}